import { GlobalIcon, UndoIcon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { useInboxEmbed } from "../context/useInboxEmbed";
import type { TranslationLanguage } from "../types";

interface TranslateComposerButtonProps {
  targetLanguage: TranslationLanguage;
  canUndo: boolean;
  isTranslating: boolean;
  disabled?: boolean;
  onTranslate: () => void;
  onUndo: () => void;
  showUndo?: boolean;
  iconSize?: IconButtonSize;
}

const labelFor = (language: TranslationLanguage): string =>
  language === "es" ? "Translate to Spanish" : "Translate to English";

export const TranslateComposerButton = ({
  targetLanguage,
  canUndo,
  isTranslating,
  disabled,
  onTranslate,
  onUndo,
  showUndo = true,
  iconSize = IconButtonSize.SM
}: TranslateComposerButtonProps) => {
  const embed = useInboxEmbed();
  const translateLabel = labelFor(targetLanguage);

  if (embed?.compact)
    return (
      <div className="flex items-center gap-3">
        <Show if={canUndo && showUndo}>
          <IconButton
            HugeIcon={UndoIcon}
            variant={IconButtonVariant.BASE}
            size={iconSize}
            onClick={onUndo}
            disabled={isTranslating}
            tooltip="Undo"
            tooltipZIndex={embed.modalZIndex}
            aria-label="Undo"
          />
        </Show>
        <IconButton
          HugeIcon={GlobalIcon}
          variant={IconButtonVariant.BASE}
          size={iconSize}
          onClick={onTranslate}
          loading={isTranslating}
          disabled={disabled || isTranslating}
          tooltip={translateLabel}
          tooltipZIndex={embed.modalZIndex}
          aria-label={translateLabel}
        />
      </div>
    );

  return (
    <div className="flex items-center gap-3">
      <Show if={canUndo && showUndo}>
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          onClick={onUndo}
          disabled={isTranslating}
        >
          Undo
        </Button>
      </Show>
      <Button
        HugeIcon={GlobalIcon}
        variant={ButtonVariant.BASE}
        size={ButtonSize.SM}
        onClick={onTranslate}
        loading={isTranslating}
        loadingMode="keep-label"
        disabled={disabled || isTranslating}
      >
        {translateLabel}
      </Button>
    </div>
  );
};
